<script lang="ts">
  import { formatMoney } from '$lib/utils/money';
  import { scheduleUndo } from '$lib/state/actionDialog.svelte';
  import { createQuery, createMutation, useQueryClient } from '@tanstack/svelte-query';
  import { ArrowLeft, MapPin, Pencil, Wrench, Users, Send, ChevronDown, Trash2 } from 'lucide-svelte';
  import { companiesApi, dealsApi, issuesApi, activitiesApi, peopleApi, sitesApi } from '$lib/api/crm';
  import { session } from '$lib/state/session.svelte';
  import { route } from '$lib/state/route.svelte';
  import { toast } from '$lib/state/toast.svelte';
  import { getErrorMessage } from '$lib/api/client';
  import EmailActivityModal from './EmailActivityModal.svelte';
  import StatStrip from './StatStrip.svelte';
  import OrderFeedbackPanel from './OrderFeedbackPanel.svelte';
  import { blocksOrderClosure } from '$lib/utils/orders';
  import TcsNumber from '../ui/TcsNumber.svelte';
  import TimelineFeed from './TimelineFeed.svelte';
  import { mergeTimeline } from '$lib/utils/timeline';
  import { emailDirection as emailDirectionOf } from '$lib/utils/emailDirection';
  import { SITE_STATUS_FLAG_LABELS } from '$lib/types';
  import type { Activity, DealSite, HotelSite, Issue, SitePhase } from '$lib/types';

  // Full-page site dashboard (GET /api/sites/:id) — a site is a kind=hotel
  // Company (Hotels vertical) or a Deal (every other vertical, e.g.
  // Lighting). isHotels picks which of the two `id` refers to and which
  // write endpoint (companiesApi vs dealsApi) the status strip's Save
  // PATCHes.
  const {
    id,
    isHotels,
    verticalId,
    onClose,
    onInvalid
  }: {
    id: number;
    isHotels: boolean;
    verticalId: number;
    onClose: () => void;
    onInvalid: () => void;
  } = $props();

  const queryClient = useQueryClient();

  const PHASE_OPTIONS: Array<{ value: SitePhase; label: string }> = [
    { value: '', label: 'Not set' },
    { value: 'contacting', label: 'Contacting' },
    { value: 'mockup', label: 'Mockup' },
    { value: 'material_supplied', label: 'Material supplied' },
    { value: 'installed', label: 'Installed' },
    { value: 'maintaining', label: 'Maintaining' },
    { value: 'closed', label: 'Closed' }
  ];
  const PHASE_BADGE: Record<string, { bg: string; fg: string; label: string }> = {
    '': { bg: '#F1F0EF', fg: '#666666', label: 'Not set' },
    contacting: { bg: '#E6E9EE', fg: '#10223A', label: 'Contacting' },
    mockup: { bg: '#FBF0DC', fg: '#8a5c00', label: 'Mockup' },
    material_supplied: { bg: '#FBF0DC', fg: '#8a5c00', label: 'Material supplied' },
    installed: { bg: '#E3F0F1', fg: '#226A73', label: 'Installed' },
    maintaining: { bg: '#EAE6F3', fg: '#5a4b8a', label: 'Maintaining' },
    closed: { bg: '#ECF8CC', fg: '#5c7a00', label: 'Closed' }
  };
  const ISSUE_STATUS_BADGE: Record<string, { bg: string; fg: string }> = {
    open: { bg: '#ECF8CC', fg: '#5c7a00' },
    in_progress: { bg: '#FBF0DC', fg: '#8a5c00' },
    resolved: { bg: '#E6E9EE', fg: '#10223A' }
  };
  const FLAG_KEYS = Object.keys(SITE_STATUS_FLAG_LABELS);

  // --- data ---
  // Header (name, phase/status, brand+owner or company) — same row shape as
  // one element of the Sites list, so no separate company/deal fetch is
  // needed just to render it.
  const siteQuery = createQuery(() => ({
    queryKey: ['site-detail', id, verticalId],
    queryFn: () => sitesApi.get(id, verticalId)
  }));
  const site = $derived(siteQuery.data);
  const hotelSite = $derived(isHotels ? (site as HotelSite | undefined) : undefined);
  const dealSite = $derived(!isHotels ? (site as DealSite | undefined) : undefined);
  // The company a Lighting deal-site's people/issues live on (its own
  // company_id); for a Hotels property, the site IS the company (id).
  const siteCompanyId = $derived(isHotels ? id : (dealSite?.company_id ?? undefined));

  // Deals: Hotels = every deal on this property; Lighting = the one deal
  // that IS this site.
  const hotelDealsQuery = createQuery(() => ({
    queryKey: ['site-deals', id],
    queryFn: () => dealsApi.list({ company_id: id, vertical_id: verticalId, per_page: 100 }),
    enabled: isHotels
  }));
  const lightingDealQuery = createQuery(() => ({
    queryKey: ['deal-detail', id],
    queryFn: () => dealsApi.get(id),
    enabled: !isHotels
  }));
  const dealsList = $derived(isHotels ? (hotelDealsQuery.data?.results ?? []) : lightingDealQuery.data ? [lightingDealQuery.data] : []);

  // People linked to the site — company_id (both verticals) or, for a
  // Hotels property, hotel_company_id (see the ?company_id= filter on
  // GET /api/people).
  const peopleQuery = createQuery(() => ({
    queryKey: ['site-people', siteCompanyId],
    queryFn: () => peopleApi.list({ company_id: siteCompanyId!, vertical_id: verticalId, per_page: 100 }),
    enabled: siteCompanyId != null
  }));

  // Issues live on the company (Lighting issues live on the deal's company —
  // issues have no deal_id of their own).
  const issuesQuery = createQuery(() => ({
    queryKey: ['site-issues', siteCompanyId],
    queryFn: () => issuesApi.list({ company_id: siteCompanyId!, vertical_id: verticalId, per_page: 100 }),
    enabled: siteCompanyId != null
  }));
  const openIssuesList = $derived((issuesQuery.data?.results ?? []).filter((i): i is Issue => i.status !== 'resolved'));

  // Timeline activities: company_id=property (hotels) or deal_id=deal
  // (lighting).
  const activitiesQuery = createQuery(() => ({
    queryKey: ['site-activities', isHotels, id],
    queryFn: () =>
      activitiesApi.list({
        vertical_id: verticalId,
        company_id: isHotels ? id : undefined,
        deal_id: isHotels ? undefined : id,
        per_page: 100
      })
  }));

  $effect(() => {
    if (siteQuery.isError) onInvalid();
  });

  // --- status strip (phase / flags / note) ---
  // Populated once from the loaded site, then left alone: a background
  // refetch (e.g. after Save) must never clobber an in-progress edit.
  let initialized = $state(false);
  let phaseForm = $state<SitePhase>('');
  let flagsForm = $state<Record<string, boolean>>({});
  let noteForm = $state('');
  $effect(() => {
    if (initialized || !site) return;
    phaseForm = site.site_phase;
    flagsForm = { ...Object.fromEntries(FLAG_KEYS.map((k) => [k, false])), ...site.site_status.flags };
    noteForm = site.site_status.note ?? '';
    initialized = true;
  });
  const activeFlagLabels = $derived(FLAG_KEYS.filter((k) => flagsForm[k]).map((k) => SITE_STATUS_FLAG_LABELS[k]));

  const saveStatusMutation = createMutation(() => ({
    mutationFn: async () => {
      if (!isHotels && blocksOrderClosure(phaseForm, lightingDealQuery.data)) throw new Error('Cannot close an installed order until feedback is submitted and confirmed by support. OFF-mode feedback may be unverified.');
      const body = { site_phase: phaseForm, site_status: { flags: flagsForm, note: noteForm } };
      return isHotels ? companiesApi.update(id, body) : dealsApi.update(id, body);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['site-detail', id] });
      queryClient.invalidateQueries({ queryKey: ['sites'] });
      toast.success('Site status saved.');
    },
    onError: (err: unknown) => toast.error(getErrorMessage(err))
  }));

  // Demoted behind a disclosure (see build brief problem 6) — the edit form
  // no longer occupies the first screen; phase itself is a compact header
  // control, flags/note stay one click away.
  let statusOpen = $state(false);

  // --- timeline: activities + open issues, merged by created_at (newest
  // first, problem 1: emails no longer render twice) — filter chips and
  // per-entry rendering live in TimelineFeed now, shared with the owner
  // dashboard.
  const timeline = $derived(mergeTimeline(activitiesQuery.data?.results ?? [], openIssuesList));

  // --- at-a-glance stat strip (problem 5) — all from data already fetched ---
  const touchpointsCount = $derived((activitiesQuery.data?.results ?? []).length);
  const lastContactAt = $derived(timeline[0]?.created_at ?? null);

  let commentText = $state('');
  const sendCommentMutation = createMutation(() => ({
    mutationFn: () =>
      activitiesApi.create({
        vertical_id: verticalId,
        type: 'note',
        body: commentText.trim(),
        company_id: isHotels ? id : siteCompanyId,
        deal_id: isHotels ? undefined : id
      }),
    onSuccess: () => {
      commentText = '';
      queryClient.invalidateQueries({ queryKey: ['site-activities', isHotels, id] });
    },
    onError: (err: unknown) => toast.error(getErrorMessage(err))
  }));
  function sendComment() {
    if (!commentText.trim()) return;
    sendCommentMutation.mutate();
  }

  // Opens the existing company/deal edit affordance in its own tab, rather
  // than rebuilding company/deal editing here (per the spec). A Hotels
  // property needs a fresh fetch for the full record (the site row is a
  // summary shape); a Lighting deal is already fully loaded above.
  async function openCompanyEdit() {
    try {
      const company = await companiesApi.get(id);
      session.requestEdit('companies', company);
      route.push({ tab: 'companies' });
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  }
  function openDealEdit() {
    if (!lightingDealQuery.data) return;
    session.requestEdit('deals', lightingDealQuery.data);
    route.push({ tab: 'deals' });
  }

  // Companies still owns delete (the row action on CompaniesPage/OwnersPage)
  // — this is the only property-delete affordance that doesn't require
  // opening that list first, since Hotels' main nav no longer surfaces it.
  const deleteMutation = createMutation(() => ({
    mutationFn: (targetId: number) => companiesApi.remove(targetId),
    onSuccess: (_, targetId) => {
      queryClient.invalidateQueries({ queryKey: ['sites'] });
      if (id === targetId) onClose();
    },
    onError: (err: unknown) => toast.error(getErrorMessage(err))
  }));
  function deleteProperty() {
    const targetId = id;
    scheduleUndo(`Delete property #${targetId} “${site?.name}”`, () => deleteMutation.mutate(targetId));
  }
  function openIssue(issueId: number) {
    route.push({ tab: 'issues', id: issueId });
  }

  let emailDetailRow = $state<Activity | null>(null);

  function timeAgo(iso: string): string {
    const ms = Date.now() - new Date(iso).getTime();
    const min = Math.floor(ms / 60000);
    if (min < 1) return 'now';
    if (min < 60) return `${min}m`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr}h`;
    const days = Math.floor(hr / 24);
    if (days < 7) return `${days}d`;
    return new Date(iso).toLocaleDateString();
  }

  function personName(first: string, last: string, email: string): string {
    return `${first} ${last}`.trim() || email || '(no name)';
  }

  // ponytail: no company-email field on the site summary row here, only a
  // people match — degrades to always 'sent' when the site has no linked
  // people with an email on file. Upgrade path: pass the resolved
  // company's own email through if the arrow ever needs to be exact here.
  function emailDirection(a: Activity): 'received' | 'sent' {
    return emailDirectionOf(a, peopleQuery.data?.results ?? []);
  }
</script>

<div class="mx-auto max-w-[1100px] px-4 py-5 md:px-8 md:py-6">
  <button type="button" class="mb-3.5 inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-toyama-teal" onclick={onClose}>
    <ArrowLeft class="h-4 w-4" /> Sites
  </button>

  {#if siteQuery.isLoading}
    <p class="tym-loading-state">Loading site&hellip;</p>
  {:else if siteQuery.isError}
    <p class="tym-error-state">{getErrorMessage(siteQuery.error)}</p>
  {:else if site}
    {@const phaseBadge = PHASE_BADGE[site.site_phase] ?? PHASE_BADGE['']}

    <div class="mb-4 flex flex-wrap items-start gap-4">
      <span class="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl" style="background:{phaseBadge.bg}; color:{phaseBadge.fg}">
        <MapPin class="h-6 w-6" />
      </span>
      <div class="min-w-0 flex-1">
        <div class="flex flex-wrap items-center gap-2.5">
          <h2 class="min-w-0 truncate font-heading text-[22px] font-bold leading-tight tracking-tight text-navy">{site.name}</h2>
          <span class="tym-badge shrink-0" style="background:{phaseBadge.bg}; color:{phaseBadge.fg}">{phaseBadge.label}</span>
          {#each activeFlagLabels as label (label)}
            <span class="tym-badge shrink-0" style="background:#FBF0DC; color:#8a5c00">{label}</span>
          {/each}
        </div>
        {#if hotelSite}
          <div class="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-[13px] text-[#666]">
            {#if hotelSite.brand_name}<span class="truncate">Brand: {hotelSite.brand_name}</span>{/if}
            {#if hotelSite.owner_name}<span class="truncate">Owner: {hotelSite.owner_name}</span>{/if}
          </div>
        {:else if dealSite}
          <div class="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-[13px] text-[#666]">
            <span class="truncate">Company: {dealSite.company_name ?? '—'}</span>
          </div>
        {/if}
        {#if noteForm}
          <p class="mt-1.5 truncate text-[12.5px] text-dark-gray" title={noteForm}>Note: {noteForm}</p>
        {/if}
      </div>
      {#if isHotels}
        <div class="flex shrink-0 gap-2">
          <button type="button" class="btn-secondary !h-10" onclick={openCompanyEdit}>
            <Pencil class="h-4 w-4" /> Edit property
          </button>
          <button type="button" class="btn-danger !h-10" onclick={deleteProperty} disabled={deleteMutation.isPending}>
            <Trash2 class="h-4 w-4" /> Delete
          </button>
        </div>
      {:else}
        <button type="button" class="btn-secondary !h-10 shrink-0" onclick={openDealEdit} disabled={!lightingDealQuery.data}>
          <Pencil class="h-4 w-4" /> Edit deal
        </button>
      {/if}
    </div>

    <!-- compact phase control + status disclosure (problem 6) -->
    <div class="tym-card mb-4 px-[18px] py-3">
      <div class="flex flex-wrap items-center gap-2.5">
        <label class="text-[11px] font-semibold uppercase tracking-[.08em] text-toyama-teal" for="site-phase">Phase</label>
        <select id="site-phase" class="input !h-9 w-auto" bind:value={phaseForm}>
          {#each PHASE_OPTIONS as opt (opt.value)}
            <option value={opt.value}>{opt.label}</option>
          {/each}
        </select>
        <button
          type="button"
          class="btn-primary !h-9"
          onclick={() => saveStatusMutation.mutate()}
          disabled={saveStatusMutation.isPending}
        >
          {saveStatusMutation.isPending ? 'Saving…' : 'Save'}
        </button>
        <span class="flex-1"></span>
        <button
          type="button"
          class="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-toyama-teal"
          onclick={() => (statusOpen = !statusOpen)}
          aria-expanded={statusOpen}
          aria-controls="site-status-panel"
        >
          Edit status
          <ChevronDown class="h-4 w-4 transition-transform {statusOpen ? 'rotate-180' : ''}" />
        </button>
      </div>

      {#if statusOpen}
        <div id="site-status-panel" class="mt-3.5 grid grid-cols-1 gap-4 border-t border-toyama-cloudy-gray pt-3.5 md:grid-cols-2">
          <div>
            <span class="label">Flags</span>
            <div class="flex flex-wrap gap-x-4 gap-y-1.5 pt-1.5">
              {#each FLAG_KEYS as key (key)}
                <label class="inline-flex items-center gap-1.5 text-[13px] text-heavy-carbon">
                  <input type="checkbox" bind:checked={flagsForm[key]} />
                  {SITE_STATUS_FLAG_LABELS[key]}
                </label>
              {/each}
            </div>
          </div>
          <div class="md:col-span-2">
            <label class="label" for="site-note">Ground note</label>
            <textarea id="site-note" rows="2" class="input" bind:value={noteForm} placeholder="Free text: what's actually happening on site"
            ></textarea>
          </div>
          <div class="md:col-span-2 flex justify-end">
            <button
              type="button"
              class="btn-primary !h-10"
              onclick={() => saveStatusMutation.mutate()}
              disabled={saveStatusMutation.isPending}
            >
              {saveStatusMutation.isPending ? 'Saving…' : 'Save status'}
            </button>
          </div>
        </div>
      {/if}
    </div>

    <!-- at-a-glance stat strip (problem 5) -->
    {#if !isHotels && lightingDealQuery.data?.order_number}
      <div class="mb-4 space-y-3"><TcsNumber value={lightingDealQuery.data.order_number} /><OrderFeedbackPanel dealId={id} /></div>
    {/if}
    <StatStrip
      stats={[
        { label: 'Open issues', value: openIssuesList.length },
        { label: 'Deals', value: dealsList.length },
        { label: 'Touchpoints', value: touchpointsCount },
        { label: 'Last contact', value: lastContactAt ? timeAgo(lastContactAt) : '—' }
      ]}
    />

    <div class="grid grid-cols-1 gap-5 lg:grid-cols-[340px_1fr]">
      <!-- left: site info -->
      <div class="flex min-w-0 flex-col gap-4">
        <div class="tym-card px-[18px] py-4">
          <div class="mb-2.5 text-[11px] font-semibold uppercase tracking-[.08em] text-toyama-teal">
            Deals on this site
          </div>
          {#each dealsList as d (d.id)}
            <div class="flex w-full items-center justify-between gap-2 py-1.5">
              <div class="min-w-0 flex-1">
                <div class="truncate text-[13px] font-semibold text-navy">{d.name}</div>
                {#if d.order_number}<TcsNumber value={d.order_number} />{/if}
                <div class="truncate text-[11.5px] capitalize text-dark-gray">{d.stage || '—'}</div>
              </div>
              <span class="shrink-0 font-heading text-[13px] font-bold text-toyama-teal">{formatMoney(d.amount, d.currency)}</span>
            </div>
          {:else}
            <p class="py-1 text-[12.5px] text-dark-gray">No deals yet.</p>
          {/each}

          <div class="mb-2.5 mt-4 flex items-center gap-2 border-t border-toyama-cloudy-gray pt-4 text-[11px] font-semibold uppercase tracking-[.08em] text-toyama-teal">
            <Users class="h-3.5 w-3.5" /> People
          </div>
          {#each peopleQuery.data?.results ?? [] as p (p.id)}
            <div class="flex min-w-0 flex-col gap-0 py-1.5">
              <span class="truncate text-[13px] font-semibold text-navy">{personName(p.first_name, p.last_name, p.email)}</span>
              <span class="truncate text-[11.5px] text-dark-gray">{p.job_title || p.email || '—'}</span>
            </div>
          {:else}
            <p class="py-1 text-[12.5px] text-dark-gray">No people linked yet.</p>
          {/each}

          <div class="mb-2.5 mt-4 flex items-center gap-2 border-t border-toyama-cloudy-gray pt-4 text-[11px] font-semibold uppercase tracking-[.08em] text-toyama-teal">
            <Wrench class="h-3.5 w-3.5" /> Open issues
          </div>
          {#each openIssuesList as issue (issue.id)}
            {@const ib = ISSUE_STATUS_BADGE[issue.status] ?? ISSUE_STATUS_BADGE.open}
            <button
              type="button"
              class="flex w-full min-w-0 items-center justify-between gap-2 rounded-lg py-1.5 text-left hover:bg-toyama-light-carbon"
              onclick={() => openIssue(issue.id)}
            >
              <span class="min-w-0 flex-1 truncate text-[13px] font-semibold text-navy">{issue.title}</span>
              <span class="tym-badge shrink-0" style="background:{ib.bg}; color:{ib.fg}">{issue.status.replace('_', ' ')}</span>
            </button>
          {:else}
            <p class="py-1 text-[12.5px] text-dark-gray">No open issues for this site.</p>
          {/each}
        </div>
      </div>

      <!-- right: unified timeline (problem 1: emails no longer duplicated in a side list) -->
      <div class="flex min-w-0 flex-col gap-2.5">
        <TimelineFeed entries={timeline} {emailDirection} onOpenIssue={openIssue} onOpenEmail={(a) => (emailDetailRow = a)} />

        <div class="tym-card mt-2 flex items-end gap-2.5 px-3.5 py-3">
          <textarea
            bind:value={commentText}
            placeholder="Log a note — e.g. mockup shared, waiting on client…"
            rows="1"
            class="input min-h-[44px] max-h-[120px] flex-1 resize-none"
          ></textarea>
          <button
            type="button"
            class="flex h-11 shrink-0 items-center gap-1.5 rounded-[11px] bg-toyama-lime px-4 font-heading text-[13px] font-bold text-navy hover:bg-[var(--tym-lime-600)] disabled:opacity-50"
            onclick={sendComment}
            disabled={sendCommentMutation.isPending || !commentText.trim()}
          >
            <Send class="h-4 w-4" />
            Log
          </button>
        </div>
      </div>
    </div>
  {/if}
</div>

{#if emailDetailRow}
  <EmailActivityModal
    activity={emailDetailRow}
    onClose={() => (emailDetailRow = null)}
    onDismissed={() => (emailDetailRow = null)}
  />
{/if}
